import React, { useState, useEffect, useCallback } from 'react';
import { 
  PrimaryButton, 
  DefaultButton, 
  TextField, 
  Stack,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  ProgressIndicator,
  Toggle,
  Text,
  MessageBar,
  MessageBarType
} from '@fluentui/react';
import { 
  DeleteRegular, 
  DocumentPdfRegular,
  StarRegular,
  StarFilled,
  ArrowDownloadRegular,
  InfoRegular,
  ChevronDownRegular,
  ChevronUpRegular
} from '@fluentui/react-icons';
import { buildApiUrl } from '../config/apiConfig';
import { authenticatedFetch } from '../utils/authApi';
import styles from './AdminPresentationTemplates.module.css';

interface Template {
  id: string;
  name: string;
  description: string;
  filename: string;
  thumbnail_url: string | null;
  created_at: string;
  created_by: string;
  is_default: boolean;
  is_active: boolean;
}

const AdminPresentationTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Upload form
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadThumbnail, setUploadThumbnail] = useState<File | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const r = await authenticatedFetch('/api/presentation-templates?include_inactive=true');
      if (!r.ok) throw new Error('Failed to load templates');
      const data = await r.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (e) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      setError('Please provide a file and name');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName.trim());
      formData.append('description', uploadDescription.trim());
      if (uploadThumbnail) {
        formData.append('thumbnail', uploadThumbnail);
      }

      const r = await authenticatedFetch('/api/presentation-templates', {
        method: 'POST',
        body: formData
      });

      const data = await r.json();
      if (!data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      // Reset form and refresh
      setUploadFile(null);
      setUploadName('');
      setUploadDescription('');
      setUploadThumbnail(null);
      setUploadDialogOpen(false);
      await loadTemplates();
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      setError(null);
      const r = await authenticatedFetch(`/api/presentation-templates/${templateId}`, {
        method: 'DELETE'
      });
      if (!r.ok) throw new Error('Delete failed');
      await loadTemplates();
    } catch (e) {
      setError('Failed to delete template');
    }
  };

  const handleSetDefault = async (templateId: string) => {
    try {
      setError(null);
      const r = await authenticatedFetch(`/api/presentation-templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_default: true })
      });
      if (!r.ok) throw new Error('Update failed');
      await loadTemplates();
    } catch (e) {
      setError('Failed to update template');
    }
  };

  const handleToggleActive = async (templateId: string, active: boolean) => {
    try {
      setError(null);
      const r = await authenticatedFetch(`/api/presentation-templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: active })
      });
      if (!r.ok) throw new Error('Update failed');
      await loadTemplates();
    } catch (e) {
      setError('Failed to update template');
    }
  };

  const handleDownload = async (templateId: string, name: string) => {
    try {
      const r = await authenticatedFetch(`/api/presentation-templates/${templateId}/download`);
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to download template');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3>Presentation Templates</h3>
          <p className={styles.subtitle}>
            Upload PowerPoint templates for users to choose when creating presentations.
            Design templates in tools like Gamma or PowerPoint, then upload them here.
          </p>
        </div>
        <PrimaryButton
          text="Upload Template"
          iconProps={{ iconName: 'Add' }}
          onClick={() => setUploadDialogOpen(true)}
        />
      </div>

      {/* Template Preparation Instructions */}
      <div className={styles.instructionsPanel}>
        <button 
          className={styles.instructionsToggle}
          onClick={() => setShowInstructions(!showInstructions)}
        >
          <InfoRegular />
          <span>Template Preparation Guidelines</span>
          {showInstructions ? <ChevronUpRegular /> : <ChevronDownRegular />}
        </button>
        
        {showInstructions && (
          <div className={styles.instructionsContent}>
            <div className={styles.instructionSection}>
              <h4>📄 File Requirements</h4>
              <ul>
                <li><strong>Format:</strong> PowerPoint (.pptx) only</li>
                <li><strong>Max size:</strong> 100 MB</li>
                <li><strong>Recommended size:</strong> Under 10 MB for best performance</li>
              </ul>
            </div>
            
            <div className={styles.instructionSection}>
              <h4>🎨 Design Guidelines</h4>
              <ul>
                <li><strong>Slide Layouts:</strong> Include at least these layouts:
                  <ul>
                    <li>"Title Slide" - for the opening slide</li>
                    <li>"Title and Content" - for regular content slides</li>
                    <li>"Section Header" - for chapter dividers (optional)</li>
                    <li>"Two Content" / "Comparison" - for side-by-side content (optional)</li>
                  </ul>
                </li>
                <li><strong>Placeholders:</strong> Use standard PowerPoint placeholders for title and content areas</li>
                <li><strong>Colors/Fonts:</strong> Set your theme colors and fonts - they will be applied automatically</li>
                <li><strong>Master Slides:</strong> Design your slide masters with headers, footers, and backgrounds</li>
              </ul>
            </div>
            
            <div className={styles.instructionSection}>
              <h4>⚠️ Important Notes</h4>
              <ul>
                <li><strong>Sample content will be removed:</strong> Only the layouts and styling are used - any slides you include will be cleared during generation</li>
                <li><strong>Keep it simple:</strong> Complex animations and transitions may not be preserved</li>
                <li><strong>Test your template:</strong> After upload, create a test presentation to verify styling</li>
                <li><strong>Thumbnail:</strong> Upload a preview image (PNG/JPG) so users can see the design</li>
              </ul>
            </div>
            
            <div className={styles.instructionSection}>
              <h4>🛠️ Creating Templates</h4>
              <ul>
                <li><strong>PowerPoint (Best):</strong> Use View → Slide Master to customize layouts with proper placeholders. This gives full control over content positioning.</li>
                <li><strong>Gamma:</strong> Exports without placeholders - backgrounds and colors are used, but text positioning is automatic. Good for quick designs.</li>
                <li><strong>Google Slides:</strong> File → Download → PowerPoint. Similar to Gamma - design elements work but no placeholder control.</li>
              </ul>
              <p className={styles.tipNote}>💡 <strong>Tip:</strong> For best results, create templates in PowerPoint using the Slide Master view with proper title and content placeholders.</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {loading ? (
        <ProgressIndicator label="Loading templates..." />
      ) : templates.length === 0 ? (
        <div className={styles.emptyState}>
          <DocumentPdfRegular className={styles.emptyIcon} />
          <Text variant="large">No templates uploaded yet</Text>
          <Text>Upload a .pptx file to create your first template</Text>
        </div>
      ) : (
        <div className={styles.templateGrid}>
          {templates.map(template => (
            <div 
              key={template.id} 
              className={`${styles.templateCard} ${!template.is_active ? styles.inactive : ''}`}
            >
              <div className={styles.templatePreview}>
                {template.thumbnail_url ? (
                  <img src={buildApiUrl(template.thumbnail_url)} alt={template.name} />
                ) : (
                  <DocumentPdfRegular className={styles.placeholderIcon} />
                )}
                {template.is_default && (
                  <div className={styles.defaultBadge}>
                    <StarFilled /> Default
                  </div>
                )}
              </div>
              
              <div className={styles.templateInfo}>
                <h4>{template.name}</h4>
                {template.description && (
                  <p className={styles.description}>{template.description}</p>
                )}
                <p className={styles.meta}>
                  Added {new Date(template.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className={styles.templateActions}>
                <Toggle
                  label="Active"
                  checked={template.is_active}
                  onChange={(_, checked) => handleToggleActive(template.id, !!checked)}
                  inlineLabel
                />
                
                <div className={styles.actionButtons}>
                  {!template.is_default && (
                    <IconButton
                      iconProps={{ iconName: 'FavoriteStar' }}
                      title="Set as default"
                      onClick={() => handleSetDefault(template.id)}
                    />
                  )}
                  <IconButton
                    title="Download"
                    onClick={() => handleDownload(template.id, template.name)}
                  >
                    <ArrowDownloadRegular />
                  </IconButton>
                  <IconButton
                    title="Delete"
                    onClick={() => handleDelete(template.id)}
                  >
                    <DeleteRegular />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog
        hidden={!uploadDialogOpen}
        onDismiss={() => setUploadDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Upload Presentation Template',
          subText: 'Upload a .pptx file with your designed layouts and styles.'
        }}
        modalProps={{ isBlocking: true }}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <div className={styles.fileInput}>
            <label>Template File (*.pptx)</label>
            <input
              type="file"
              accept=".pptx"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
            {uploadFile && <span className={styles.fileName}>{uploadFile.name}</span>}
          </div>

          <TextField
            label="Template Name"
            required
            value={uploadName}
            onChange={(_, v) => setUploadName(v || '')}
            placeholder="e.g., Corporate Blue"
          />

          <TextField
            label="Description"
            multiline
            rows={2}
            value={uploadDescription}
            onChange={(_, v) => setUploadDescription(v || '')}
            placeholder="Modern corporate theme with blue accents"
          />

          <div className={styles.fileInput}>
            <label>Preview Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setUploadThumbnail(e.target.files?.[0] || null)}
            />
            {uploadThumbnail && <span className={styles.fileName}>{uploadThumbnail.name}</span>}
          </div>
        </Stack>

        <DialogFooter>
          <DefaultButton text="Cancel" onClick={() => setUploadDialogOpen(false)} />
          <PrimaryButton 
            text={uploading ? 'Uploading...' : 'Upload'} 
            onClick={handleUpload}
            disabled={uploading || !uploadFile || !uploadName.trim()}
          />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default AdminPresentationTemplates;
